/**
 * VideoDateButton - "Join Video Date" button for speed dating rounds
 * 
 * Features:
 * - Creates a video room for the pair
 * - Gets a meeting token
 * - Launches the Daily Prebuilt video call
 * 
 * SECURITY: Requires identity verification
 * - Unverified users see "Verify to Join" button
 * - Click redirects to verification flow with return URL
 * - Backend also enforces (403 if not verified)
 */

import React, { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createVideoRoom, getVideoToken, checkVideoEligibility } from '../../api/video';
import DailyVideoCall from './DailyVideoCall';
import { AuthContext } from '../../App';

/**
 * @param {Object} props
 * @param {string} props.eventId - Speed dating event ID
 * @param {number} props.roundIndex - Current round index
 * @param {string} props.partnerId - Partner user ID (for room naming)
 * @param {boolean} [props.disabled] - Disable the button
 * @param {() => void} [props.onCallEnded] - Callback when call ends
 */
export default function VideoDateButton({
  eventId,
  roundIndex,
  partnerId,
  disabled = false,
  onCallEnded,
}) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [error, setError] = useState('');
  const [videoSession, setVideoSession] = useState(null);
  const connectingRef = useRef(false);

  // Check eligibility on mount
  useEffect(() => {
    let mounted = true;
    
    async function checkEligibility() {
      try {
        const result = await checkVideoEligibility();
        if (mounted) {
          setEligible(result.eligible);
          setCheckingEligibility(false);
        }
      } catch (err) {
        console.error('[VideoDateButton] Eligibility check failed:', err);
        if (mounted) {
          // Assume not eligible on error (fail-safe)
          setEligible(false);
          setCheckingEligibility(false);
        }
      }
    }
    
    if (user?.id) {
      checkEligibility();
    } else {
      setCheckingEligibility(false);
      setEligible(false);
    }
    
    return () => { mounted = false; };
  }, [user?.id]);

  // Handle verification redirect
  const handleVerifyRedirect = useCallback(() => {
    // Track analytics
    console.log('[VideoDateButton] Verify CTA clicked - redirecting to verification');
    
    // Build return URL
    const returnUrl = encodeURIComponent(location.pathname);
    navigate(`/verify-age?return=${returnUrl}`);
  }, [navigate, location.pathname]);

  const handleStartVideo = useCallback(async () => {
    // Double-click guard
    if (connectingRef.current || loading) return;
    connectingRef.current = true;
    
    setLoading(true);
    setError('');

    try {
      // CRITICAL: Both users must generate the SAME room name
      const myId = user?.id || '';
      const pairIds = [myId.slice(0, 8), partnerId.slice(0, 8)].sort();
      const sortedPairId = pairIds.join('-');
      const roomName = `sd-${eventId.slice(0, 8)}-r${roundIndex}-${sortedPairId}`;
      
      console.log('[VideoDateButton] Creating room:', roomName);
      
      // Create room (5 min TTL for speed dating rounds)
      const room = await createVideoRoom({
        room_name: roomName,
        ttl_seconds: 300,
        max_participants: 2,
      });

      // Get meeting token
      const tokenData = await getVideoToken(room.room_name, {
        ttl_seconds: 300,
      });

      setVideoSession({
        roomUrl: tokenData.room_url,
        token: tokenData.token,
        roomName: room.room_name,
      });
    } catch (err) {
      console.error('[VideoDateButton] Error starting video:', err);
      
      // Handle verification required error from backend
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail?.error === 'identity_verification_required') {
          setEligible(false);
          setError('Identity verification required for video dates');
          return;
        }
      }
      
      setError(err.response?.data?.detail?.message || err.message || 'Failed to start video call');
    } finally {
      setLoading(false);
      connectingRef.current = false;
    }
  }, [eventId, roundIndex, partnerId, user, loading]);

  const handleLeaveVideo = useCallback(() => {
    setVideoSession(null);
    onCallEnded?.();
  }, [onCallEnded]);

  const handleVideoError = useCallback((err) => {
    console.error('[VideoDateButton] Video error:', err);
    setError(err.message || 'Video call error');
    setVideoSession(null);
  }, []);

  // Show video call interface when session is active
  if (videoSession) {
    return (
      <div 
        className="mt-4" 
        data-testid="video-session-active"
        data-room-name={videoSession.roomName}
      >
        {/* Hidden element for E2E room assertion */}
        <div 
          data-testid="daily-room-name" 
          data-value={videoSession.roomName}
          className="hidden"
          aria-hidden="true"
        />
        <DailyVideoCall
          roomUrl={videoSession.roomUrl}
          token={videoSession.token}
          onLeave={handleLeaveVideo}
          onError={handleVideoError}
          className="aspect-video"
        />
      </div>
    );
  }

  // Loading eligibility check
  if (checkingEligibility) {
    return (
      <div className="mt-4" data-testid="video-eligibility-loading">
        <div className="w-full px-6 py-3 rounded-2xl bg-gray-700/50 flex items-center justify-center gap-2">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          <span className="text-white/70 text-sm">Checking video access...</span>
        </div>
      </div>
    );
  }

  // NOT ELIGIBLE: Show verification gate
  if (!eligible) {
    return (
      <div className="mt-4" data-testid="video-verification-gate">
        {error && (
          <div 
            className="mb-3 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-2 text-sm text-red-200"
            data-testid="video-error-message"
          >
            {error}
          </div>
        )}

        <button
          type="button"
          data-testid="verify-to-join-video-btn"
          onClick={handleVerifyRedirect}
          disabled={disabled}
          className="w-full px-6 py-3 rounded-2xl text-base font-semibold
                     bg-gradient-to-r from-amber-500 to-orange-500 text-white
                     shadow-lg shadow-amber-500/30
                     hover:brightness-110 transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
        >
          <span className="text-xl">🔐</span>
          Verify to Join Video
        </button>

        <p className="mt-2 text-xs text-white/50 text-center">
          Identity verification required for video dates • Takes 2 min
        </p>
      </div>
    );
  }

  // ELIGIBLE: Show join button
  return (
    <div className="mt-4">
      {error && (
        <div 
          className="mb-3 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-2 text-sm text-red-200"
          data-testid="video-error-message"
        >
          {error}
        </div>
      )}

      <button
        type="button"
        data-testid="join-video-date-btn"
        data-state={loading ? "connecting" : "ready"}
        aria-busy={loading}
        onClick={handleStartVideo}
        disabled={disabled || loading || !user?.id}
        className="w-full px-6 py-3 rounded-2xl text-base font-semibold
                   bg-gradient-to-r from-blue-500 to-cyan-500 text-white
                   shadow-lg shadow-blue-500/30
                   hover:brightness-110 transition-all
                   disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <span className="text-xl">📹</span>
            Join Video Date
          </>
        )}
      </button>

      <p className="mt-2 text-xs text-white/50 text-center">
        Video is private • No recording • Auto-ends with round
      </p>
    </div>
  );
}
